import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight, ScanFace } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authApi, setToken } from "@/lib/auth";
import Seo from "@/components/Seo";
import {
  isPasskeySupported, isPlatformAuthenticatorAvailable,
  passkeyAvailable, loginWithPasskey,
} from "@/lib/webauthn";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const nav = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("wl_admin_last_email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasskey, setShowPasskey] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isPasskeySupported()) return;
      const platform = await isPlatformAuthenticatorAvailable();
      if (!platform) return;
      const server = await passkeyAvailable({ apiBase: API_BASE });
      setShowPasskey(server);
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.login(email.trim(), password);
      localStorage.setItem("wl_admin_last_email", email.trim());
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const signInWithFaceId = async () => {
    const targetEmail = (email || localStorage.getItem("wl_admin_last_email") || "").trim();
    if (!targetEmail) {
      toast.info("Enter your email first so we know which passkey to use.");
      return;
    }
    setPasskeyBusy(true);
    try {
      const r = await loginWithPasskey({ email: targetEmail, apiBase: API_BASE });
      if (r.cancelled) return;
      if (r.ok && r.access_token) {
        setToken(r.access_token);
        localStorage.setItem("wl_admin_last_email", targetEmail);
        toast.success("Signed in.");
        nav("/admin");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Face ID sign-in failed.");
    } finally { setPasskeyBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center p-6">
      <Seo title="Admin Sign In" noindex path="/admin/login" />
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="max-w-md w-full bg-white border border-[#DDD1BE] rounded-2xl shadow-sm p-10 md:p-12"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#6A5F58] mb-8 link-underline font-brand"
          data-testid="login-back-home"
        >
          Back to Walnut Lakes
        </Link>

        <div className="w-12 h-12 rounded-full bg-[#0F0E0E] text-[#F5EFE6] flex items-center justify-center mb-6">
          <Lock size={18} strokeWidth={1.5} />
        </div>

        <h1 className="font-serif text-4xl md:text-5xl text-[#14100F] leading-[1.0]">
          Admin sign in
        </h1>
        <p className="mt-3 text-sm text-[#6A5F58]">
          Access bookings, availability blocks &amp; site content.
        </p>

        <div className="mt-8 space-y-5">
          <div>
            <Label htmlFor="al-email" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">
              Email
            </Label>
            <Input
              id="al-email"
              type="email"
              autoComplete="email webauthn"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 bg-[#F5EFE6] h-11 rounded-lg"
              required
              data-testid="login-email"
            />
          </div>
          <div>
            <Label htmlFor="al-password" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">
              Password
            </Label>
            <Input
              id="al-password"
              type="password"
              autoComplete="current-password webauthn"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 bg-[#F5EFE6] h-11 rounded-lg"
              required={!showPasskey}
              data-testid="login-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="btn-primary mt-8 w-full justify-center font-brand tracking-[0.14em] disabled:opacity-60"
        >
          {loading ? "Signing in..." : (<>Sign in <ArrowRight size={16} /></>)}
        </button>

        {showPasskey && (
          <>
            <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-[#6A5F58] font-brand">
              <span className="flex-1 h-px bg-[#DDD1BE]" /> or <span className="flex-1 h-px bg-[#DDD1BE]" />
            </div>
            <button
              type="button"
              onClick={signInWithFaceId}
              disabled={passkeyBusy}
              data-testid="login-passkey"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition font-brand tracking-[0.14em] text-xs uppercase disabled:opacity-50"
            >
              <ScanFace size={14} /> {passkeyBusy ? "Verifying…" : "Sign in with Face ID"}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default AdminLogin;
